import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as ChargingStationIcon } from '../../../assets/icons/sidebar/sidebar-station.svg';
import { AvailableTabProperties } from '../../../interface/station-management-side-panel.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { setSelectedStation } from '../../../redux/slices/StationManagementSidePanelSlice';

import StationComparison from './components/StationComparison';
import StationDashboard from './components/StationDashboard';
import StationManagementTabs from './components/StationManagementTabs';
import StationPerformance from './components/StationPerformance';
import StationReports from './components/StationReports';
import StationTickets from './components/StationTickets';

/**
 * Station Management
 * @returns {any} The rendered component
 */
function StationManagementSidePanel() {
  const { i18n } = useTranslation('sidePanel');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tab: deviceTab } = useParams<{ tab: string }>();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const { id } = useParams();
  const [searchParameters] = useSearchParams();
  const { t } = useTranslation('station');

  const searchBy = searchParameters.get('searchBy'); // "New station"
  const [containerTabs, setContainerTabs] = useState<AvailableTabProperties[]>([
    {
      label: t('stationDashboard'),
      key: 'station-dashboard',
      isActive: false,
    },
    {
      label: t('stationPerformance'),
      key: 'station-performance',
      isActive: false,
    },
    {
      label: t('reports'),
      key: 'reports',
      isActive: false,
    },
    {
      label: t('stationComparison'),
      key: 'station-comparison',
      isActive: false,
    },
    // {
    //   label: t('tickets'),
    //   key: 'tickets',
    //   isActive: false,
    // },
  ]);

  useEffect(() => {
    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        let { label } = tab;
        if (tab.key === 'station-dashboard') label = t('stationDashboard');
        if (tab.key === 'station-performance') label = t('stationPerformance');
        if (tab.key === 'reports') label = t('reports');
        if (tab.key === 'station-comparison') label = t('stationComparison');
        if (tab.key === 'tickets') label = t('tickets');
        return {
          ...tab,
          label,
        };
      }),
    );
  }, [t]);

  /**
   * Function to switch the tab
   * @param {string} key - The tab key
   * @param {boolean} navigateRequire - Whether navigation is required
   */
  const handleSwitchTab = (key: string, navigateRequire: boolean = true) => {
    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          if (navigateRequire) {
            const basePath = location.pathname.includes('station-expanded')
              ? 'station-expanded'
              : 'station-management';
            navigate(
              {
                pathname: `/ev-admin/${basePath}/${selectedChargingPoint?.id}/${tab.key}`,
                search: `?searchBy=${selectedChargingPoint?.name}`,
              },
              {
                state: {
                  source:
                    location.state?.source ||
                    new URLSearchParams(location.search).get('source') ||
                    location.pathname,
                },
              },
            );
          }

          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  useEffect(() => {
    handleSwitchTab(deviceTab || 'station-dashboard', false);
  }, [deviceTab]);

  useEffect(() => {
    if (!id) return;
    if (selectedChargingPoint?.id === id) return;
    dispatch(
      setSelectedStation({
        id,
        name: searchBy,
      }),
    );
  }, [id]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('stationManagement', 'Station Management'),
        icon: <ChargingStationIcon />,
      }),
    );
  }, [t]);

  return (
    <div className="station-management-side-panel-wrap" dir={i18n.dir()}>
      <div className="station-management-container">
        <StationManagementTabs containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
        <div className="station-management-content">
          {(() => {
            const activeTab = containerTabs.find((tab) => tab.isActive)?.key || 'device-details';

            let deviceContent = null;
            switch (activeTab) {
              case 'station-dashboard':
                deviceContent = <StationDashboard />;
                break;
              case 'station-performance':
                deviceContent = <StationPerformance />;
                break;
              case 'reports':
                deviceContent = <StationReports />;
                break;
              case 'station-comparison':
                deviceContent = <StationComparison />;
                break;
              case 'tickets':
                deviceContent = <StationTickets />;
                break;
              default:
                deviceContent = <StationDashboard />;
                break;
            }
            return deviceContent;
          })()}
        </div>
      </div>
    </div>
  );
}

export default StationManagementSidePanel;
